import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TriangleAlert, Globe, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AuthService } from '@/services/core/auth'
import { NetworksApi } from '@/services/networks/networksApi'
import { useNetwork } from '../contexts/NetworkContext'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'

interface NetworkObjectLookupProps {
  objectId: string
}

export function NetworkObjectLookup({ objectId }: NetworkObjectLookupProps) {
  const { t } = useTranslation()
  const hasToken = AuthService.isAuthenticated()
  const { currentNetwork, switchNetwork } = useNetwork()

  const { data: networks = [], isLoading, isError } = useQuery({
    queryKey: ['networks', 'object', objectId],
    queryFn: () => NetworksApi.getForObject(objectId),
    enabled: !!objectId && hasToken,
  })

  return (
    <div className="ad-page">
    <Card tone="danger">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={TriangleAlert} />
          {t('networkLookup.notFoundTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p style={{ margin: '0 0 16px', color: 'var(--ad-text-muted)' }}>
          {t('networkLookup.notFoundDescription', { id: objectId, network: currentNetwork?.name ?? 'the current network' })}
        </p>

        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Skeleton className="h-4 w-3/5" />
            <Skeleton className="h-4 w-2/5" />
          </div>
        )}

        {isError && (
          <p style={{ color: 'var(--ad-danger)' }}>
            {t('networkLookup.lookupFailed')}
          </p>
        )}

        {!isLoading && !isError && networks.length === 0 && (
          <p style={{ color: 'var(--ad-text-muted)' }}>
            {t('networkLookup.notInAnyNetwork')}
          </p>
        )}

        {!isLoading && networks.length > 0 && (
          <>
            <p style={{ margin: '0 0 12px' }}>
              {t('networkLookup.foundInNetworks', { count: networks.length })}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {networks.map((n) => (
                <div
                  key={n.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: 'var(--ad-bg)',
                    border: '1px solid var(--ad-border)',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Globe className="size-4" />
                    {n.name}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => switchNetwork(n.tagId)}
                  >
                    <ArrowRight className="size-4" />
                    {t('common.switch')}
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
    </div>
  )
}
